import React, { useCallback, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Alert, Box, IconButton, Tooltip, Typography } from "@mui/material";
import TableServer from "@/components/tables/TableServer";
import RpSvgIcon from "@/components/icons/RpSvgIcon";
import LocationStatusCell from "./LocationStatusCell";
import LocationEditHistoryModal from "./LocationEditHistoryModal";
import { getTargetingTypeLabel } from "../utils/locationManagementConstants";
import { normalizeLocationGroupsByType } from "../utils/locationManagementGroupTypes";

type LocationManagementRow = {
  fk_location_id?: string | number;
  fk_participant_id?: string | number;
  system_generated_location_id?: string | number;
  location_name?: string;
  location_status?: string;
  participant_first_name?: string;
  participant_last_name?: string;
  participant_email?: string;
  street_address?: string;
  city?: string;
  state?: string;
  zip_code?: string | number;
  country?: string;
  group_name?: string | string[];
  location_brand_unique_id?: string | number;
  landing_page_url?: string;
  radius?: string | number | null;
  geotarget?: string;
  franchise_group?: string;
  location_created_at?: string | number | Date;
  location_updated_at?: string | number | Date;
  [key: string]: unknown;
};

type LocationManagementPagination = {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
};

type TableCellContext = {
  value?: unknown;
  row: { original: LocationManagementRow };
};

type TableFetchParams = {
  pageIndex: number;
  sortBy?: string;
  sortByDir?: string;
};

type TableColumn = {
  Header: string;
  accessor: string;
  width?: number;
  disableSortBy?: boolean;
  Cell?: (_context: TableCellContext) => ReactNode;
};

type LocationManagementTableProps = {
  brandId?: string | number;
  data: LocationManagementRow[];
  isLoading: boolean;
  pagination: LocationManagementPagination;
  onPageChange: (_page: number) => void;
  onSortChange: (_sortBy?: string, _sortByDir?: string) => void;
  onEditClick: (_location: LocationManagementRow) => void;
};

const GROUPS_CELL_VISIBLE_COUNT = 2;
const GROUPS_CELL_PILL_SX = {
  alignItems: "center",
  bgcolor: "var(--neutral-100)",
  borderRadius: "var(--radius-round)",
  color: "var(--neutral-700)",
  display: "inline",
  fontSize: 12,
  fontWeight: 400,
  height: 24,
  justifyContent: "flex-start",
  lineHeight: "23.5px",
  minWidth: 0,
  overflow: "hidden",
  px: 1,
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const GROUPS_CELL_TOOLTIP_PROPS = {
  tooltip: {
    sx: {
      bgcolor: "var(--surface-dark)",
      borderRadius: "var(--radius-container)",
      color: "var(--neutral-white)",
      fontSize: 12,
      fontWeight: 400,
      lineHeight: "18px",
      px: 2,
      py: 1.25,
    },
  },
  arrow: {
    sx: {
      color: "var(--surface-dark)",
    },
  },
};

const GroupsByTypeCell = ({
  location,
}: {
  location: LocationManagementRow;
}) => {
  const sections = normalizeLocationGroupsByType(location);
  const groupNames = sections.flatMap((section) => section.names);

  if (!groupNames.length) {
    return "-";
  }

  const visibleGroups = groupNames.slice(0, GROUPS_CELL_VISIBLE_COUNT);
  const hiddenGroups = groupNames.slice(GROUPS_CELL_VISIBLE_COUNT);
  const allGroupsLabel = groupNames.join(", ");
  const hiddenGroupsLabel = hiddenGroups.join(", ");

  return (
    <Box sx={{ alignItems: "center", display: "flex", gap: "5px", p: 0 }}>
      {visibleGroups.map((groupName, index) => (
        <Tooltip
          key={`${groupName}-${index}`}
          title={allGroupsLabel}
          arrow
          slotProps={GROUPS_CELL_TOOLTIP_PROPS}
        >
          <Box
            component="span"
            sx={{
              ...GROUPS_CELL_PILL_SX,
              maxWidth: visibleGroups.length > 1 ? 112 : 168,
            }}
          >
            {groupName}
          </Box>
        </Tooltip>
      ))}
      {hiddenGroups.length ? (
        <Tooltip
          title={hiddenGroupsLabel}
          arrow
          slotProps={GROUPS_CELL_TOOLTIP_PROPS}
        >
          <Typography
            component="span"
            sx={{
              color: "var(--palette-text-primary)",
              cursor: "default",
              fontSize: 12,
              fontWeight: 400,
              lineHeight: "18px",
              whiteSpace: "nowrap",
            }}
          >
            (+{hiddenGroups.length})
          </Typography>
        </Tooltip>
      ) : null}
    </Box>
  );
};

const displayOptionalValue = (value) =>
  value === "" || value == null ? "-" : value;

const displayTargetingSameAsLocation = (value) =>
  value === 1 ? "Yes" : value === 0 ? "No" : displayOptionalValue(value);

const LocationManagementTable = ({
  brandId,
  data,
  isLoading,
  pagination,
  onPageChange,
  onSortChange,
  onEditClick,
}: LocationManagementTableProps) => {
  const skeletonRowCount = pagination?.pageSize || 10;
  const [historyLocation, setHistoryLocation] =
    useState<LocationManagementRow | null>(null);
  const isHistoryOpen = !!historyLocation;
  const columns = useMemo<TableColumn[]>(
    () => [
      {
        Header: "Location Name",
        accessor: "location_name",
        width: 220,
        Cell: ({ row }) => (
          <Link
            to={`/dashboard/location-report/${row.original.system_generated_location_id}/${row.original.fk_participant_id}`}
            className="link text-break d-inline-flex align-items-start"
          >
            <RpSvgIcon
              name="location_report"
              size={18}
              className="flex-shrink-0 mr-2 "
            />
            <span>{row.original.location_name}</span>
          </Link>
        ),
      },
      {
        Header: "Participant First Name",
        accessor: "participant_first_name",
      },
      {
        Header: "Participant Last Name",
        accessor: "participant_last_name",
      },
      {
        Header: "Status",
        accessor: "location_status",
        width: 200,
        Cell: ({ value, row }) => (
          <LocationStatusCell
            brandId={brandId}
            value={value as string | undefined}
            location={row.original}
          />
        ),
      },
      {
        Header: "Email Address",
        width: 200,
        accessor: "participant_email",
      },
      {
        Header: "Location Street Address",
        accessor: "street_address",
      },
      { Header: "City", accessor: "city" },
      { Header: "State", accessor: "state" },
      { Header: "Zip Code", accessor: "zip_code", width: 100 },
      { Header: "Country", accessor: "country", width: 100 },
      {
        Header: "Groups",
        accessor: "groups_by_type",
        disableSortBy: true,
        width: 260,
        Cell: ({ row }) => <GroupsByTypeCell location={row.original} />,
      },
      {
        Header: "Store/Location Number (Unique ID)",
        width: 160,
        accessor: "location_brand_unique_id",
        Cell: ({ value }) => (value ? String(value) : "-"),
      },
      {
        Header: "Landing Page URL",
        accessor: "landing_page_url",
        width: 300,
        Cell: ({ value }) => (value ? String(value) : "-"),
      },
      {
        Header: "Targeting Type",
        accessor: "targeting_type",
        width: 150,
        Cell: ({ value }) => getTargetingTypeLabel(value),
      },
      {
        Header: "Radius Targeting",
        accessor: "radius",
        width: 100,
        Cell: ({ value }) => (value ? `${value} ` : "-"),
      },
      {
        Header: "Zip Code Targeting",
        accessor: "geotarget",
        width: 140,
        Cell: ({ value }) => (value ? String(value) : "-"),
      },
      {
        Header: "Franchise Group",
        accessor: "franchise_group",
        width: 140,
        Cell: ({ value }) => value || "-",
      },
      {
        Header: "DMA",
        accessor: "dma",
        width: 140,
        Cell: ({ value }) => value || "-",
      },
      {
        Header: "Competitors",
        accessor: "competitors",
        width: 180,
        Cell: ({ value }) => value || "-",
      },
      {
        Header: "Local Context",
        accessor: "local_context",
        width: 180,
        Cell: ({ value }) => value || "-",
      },
      {
        Header: "Primary Business Goal",
        accessor: "primary_business_goal",
        width: 200,
        Cell: ({ value }) => value || "-",
      },
      {
        Header: "Daypart Focus",
        accessor: "daypart_focus",
        width: 160,
        Cell: ({ value }) => value || "-",
      },
      {
        Header: "Latitude",
        accessor: "location_lat",
        width: 120,
        Cell: ({ value }) => displayOptionalValue(value),
      },
      {
        Header: "Longitude",
        accessor: "location_long",
        width: 120,
        Cell: ({ value }) => displayOptionalValue(value),
      },
      {
        Header: "Targeting Same as Location",
        accessor: "targeting_same_as_location",
        width: 180,
        Cell: ({ value }) => displayTargetingSameAsLocation(value),
      },
      {
        Header: "Targeting Latitude",
        accessor: "targeting_latitude",
        width: 150,
        Cell: ({ value }) => displayOptionalValue(value),
      },
      {
        Header: "Targeting Longitude",
        accessor: "targeting_longitude",
        width: 150,
        Cell: ({ value }) => displayOptionalValue(value),
      },
      {
        Header: "Created Date",
        accessor: "location_created_at",
        width: 120,
        Cell: ({ value }) =>
          value
            ? new Date(value as string | number | Date).toLocaleDateString()
            : "-",
      },
      {
        Header: "Last Updated Date",
        accessor: "location_updated_at",
        width: 140,
        Cell: ({ value, row }) =>
          value ? (
            <Box
              component="button"
              type="button"
              aria-label="View edit history"
              onClick={() => setHistoryLocation(row.original)}
              sx={{
                appearance: "none",
                background: "none",
                border: 0,
                color: "primary.main",
                cursor: "pointer",
                display: "inline",
                font: "inherit",
                fontWeight: 700,
                p: 0,
                textAlign: "left",
                textDecorationColor: (theme) =>
                  `${theme.palette.primary.main}73`,
                textDecorationLine: "underline",
                textDecorationThickness: "1px",
                textUnderlineOffset: "2px",
              }}
            >
              {new Date(value as string | number | Date).toLocaleDateString()}
            </Box>
          ) : (
            "-"
          ),
      },
      {
        Header: "Action",
        accessor: "action",
        disableSortBy: true,
        width: 100,
        Cell: ({ row }) => (
          <IconButton
            size="small"
            onClick={() => onEditClick(row.original)}
            title="Edit Location"
          >
            <RpSvgIcon name="location_listing_edit" size={25} />
          </IconButton>
        ),
      },
    ],
    [brandId, onEditClick]
  );

  const fetchData = useCallback(
    ({ pageIndex, sortBy, sortByDir }: TableFetchParams) => {
      onPageChange(pageIndex + 1);
      onSortChange(sortBy, sortByDir);
    },
    [onPageChange, onSortChange]
  );

  return (
    <>
      <TableServer
        columns={columns}
        data={data}
        isLoading={isLoading}
        pageCount={pagination.totalPages}
        totalRecords={pagination.totalRecords}
        initialPageSize={pagination.pageSize}
        initialPage={pagination.currentPage - 1}
        fetchData={fetchData}
        showPagination
        showPageSize={false}
        showSearch={false}
        useLayout
        containerClass="location-management-table"
        showSkeletonLoader
        skeletonRowCount={skeletonRowCount}
        emptyState={
          <Alert severity="info" className="my-0 text-left">
            No locations found. Try changing the search or filters, or add a new
            location.
          </Alert>
        }
      />
      <LocationEditHistoryModal
        brandId={brandId}
        open={isHistoryOpen}
        onClose={() => setHistoryLocation(null)}
        location={historyLocation}
      />
    </>
  );
};

export default LocationManagementTable;
